<template>
    <div class="d-flex flex-column" style="height: 48em">
        <!-- Header -->
        <header class="flex-shrink-0 p-3 border-bottom">
            <h2 class="editor-header">{{ $t('contract_sanction.title') }}</h2>
        </header>
        <!-- Content -->
        <main class="flex-grow-1 overflow-auto p-3">
            <div class="row mb-2" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('common.id') }}</span>
                    <div>{{ contractSanction.id }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.status') }}</span>
                    <div><span :class="statusClass(contractSanction.status)">{{ getStatusText(contractSanction.status) }}</span></div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.date') }}</span>
                    <div>{{ formatDate(contractSanction.created) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.sum') }}</span>
                    <div>{{ formatCurrency(contractSanction.total_sum / 100) }}</div>
                </div>
            </div>
            <div class="row mb-2" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contractor_number') }}</span>
                    <div>{{ contractSanction.contractor_number || '-' }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.gross_amount') }}</span>
                    <div>{{ formatGrossAmount(contractSanction.gross_amount) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.document_date') }}</span>
                    <div>{{ formatDate(contractSanction.document_date) }}</div>
                </div>
            </div>
            <div class="row mb-2" style="font-size: .9em">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contract') }}</span>
                    <div>{{ contractIdName }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contract_number') }}</span>
                    <div>{{ contractSanction.contract?.contract_number ? contractSanction.contract.contract_number : '-' }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.customer') }}</span>
                    <div>{{ contractSanction.contract?.customer?.name || '-' }}</div>
                </div>
            </div>
            <!-- Invoicing row -->
            <hr/>
            <h6 class="mb-3" style="color: #5f8f01"><i class="fas fa-list me-2"></i>{{ $t('invoice_history.invoice_rows') }}</h6>
            <div v-if="hasValidRow" class="table-responsive">
                <table class="table table-sm">
                    <thead>
                    <tr>
                        <th>{{ $t('common.id') }}</th>
                        <th>{{ $t('common.time_range') }}</th>
                        <th>{{ $t('orders.order') }}</th>
                        <th>{{ $t('account.account') }}</th>
                        <th>{{ $t('account.invoicing_code') }}</th>
                        <th>{{ $t('material_reports.unit') }}</th>
                        <th>{{ $t('daily_summary.invoiceable_amount') }}</th>
                        <th class="text-end">{{ $t('invoice_history.sum') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>{{ sanctionRow.id }}</td>
                        <td>{{ rowTimeRange }}</td>
                        <td>{{ sanctionRow.info || '-' }}</td>
                        <td>{{ rowAccountText }}</td>
                        <td>{{ rowInvoicingCodeText }}</td>
                        <td>{{ rowUnitText }}</td>
                        <td>{{ rowAmount }}</td>
                        <td class="text-end">{{ formatCurrency(contractSanction.total_sum / 100) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="text-muted small">
                {{ $t('contract_sanction.invalid_row_data') }}
            </div>
        </main>

        <!-- Footer -->
        <footer class="flex-shrink-0 p-3 border-top">
            <div class="col-sm-12 my2 d-flex justify-content-end">
                <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                    {{ $t('common.close') }}
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {priceHelper} from "../mixins/PricingMixin.js";
import {invoicingHelper} from "../mixins/InvoicingMixin.js";

export default {
    name: 'ContractSanctionDetails',
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper],
    props: {
        contractSanction: {
            type: Object,
            required: true
        }
    },
    computed: {
        contractIdName() {
            const contract = this.contractSanction.contract
            const id = contract?.id ?? '-'
            const name = contract?.name || '-'
            return id + ' - ' + name
        },
        // The real GET /invoice payload for a contract sanction carries
        // its row data under invoice_rows[0] (id, start_date, end_date,
        // info, account, code) - not a separate `rows` array (confirmed
        // against backend data, ROUTA-5584). There is no row-level
        // unit_price/amount field: unit is always "pcs" and amount is
        // always 1 per the feature's fixed pricing model, and the sum
        // shown is the record's top-level total_sum.
        hasValidRow() {
            return this.contractSanction.invoice_rows?.length === 1
        },
        sanctionRow() {
            return this.hasValidRow ? this.contractSanction.invoice_rows[0] : null
        },
        rowTimeRange() {
            if (!this.hasValidRow) return ''
            return this.formatDate(this.sanctionRow.start_date) + ' - ' + this.formatDate(this.sanctionRow.end_date)
        },
        rowAccountText() {
            if (!this.hasValidRow) return '-'
            const account = this.sanctionRow.account
            if (!account) return '-'
            return (account.code ?? '') + ' - ' + (account.name ?? '')
        },
        rowInvoicingCodeText() {
            if (!this.hasValidRow) return '-'
            const code = this.sanctionRow.code
            if (!code) return '-'
            return (code.code ?? '') + ' - ' + (code.name ?? '')
        },
        rowUnitText() {
            if (!this.hasValidRow) return '-'
            return this.pricingModelToText({unit1: 3})
        },
        rowAmount() {
            return this.hasValidRow ? 1 : '-'
        }
    }
}
</script>

<style scoped>
.badge {
    padding: 0.4em 0.6em;
    font-size: 0.85em;
}

@media (max-width: 768px) {
    .row.mb-2 > [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
    }
}
</style>
